import errorHandler from './utils/errorHandler';
import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  Tab,
  Tabs,
  Snackbar,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Chip,
  Badge
} from '@mui/material';
import api from './axiosConfig';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Checkbox
} from '@mui/material';
import { useAuth } from './AuthContext';
import SupplierBillsTab from './SupplierBillsTab';
import { useSearchParams } from 'react-router-dom';


const SUPPLIER_TYPES = ['Supplies', 'Services', 'Products', 'Payroll'];

const SUPPLIER_CATEGORIES = [
   'Accountancy & Legal', 'Bank Charges & Interest', 'Card Machine Fees',
   'Client Comfort', 'Communications', 'Electric', 'Furniture & Equipment',
   'Gas', 'Insurance', 'IT & Software', 'Leasing and Finance', 'Licences',
   'Loan Repayment', 'Maintenance & Repairs', 'Marketing & Advertising',
   'Office Supplies', 'Payroll', 'Personal', 'Postage and Courier', 'Rates',
   'Rent', 'Staff Welfare', 'Stock', 'Subscriptions', 'Sundry', 'Taxation',
   'Training', 'Utilities', 'Waste Disposal', 'Water', 'QS Rent',
   'QS Utilities', 'QS Insurance', 'QS Other'
 ];

const validationSchema = Yup.object({
  type: Yup.string().required('Type is required'),
  supplier_name: Yup.string().required('Supplier name is required'),
  contact_number: Yup.string().required('Contact number is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  address: Yup.string().required('Address is required'),
  vat_number: Yup.string(),
  account_number: Yup.string()
});

const SupplierCard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [unpaidBillsCount, setUnpaidBillsCount] = useState(0);
  const [searchParams] = useSearchParams();

  const { user } = useAuth();
  const isOwner = String(user?.role || '').toUpperCase() === 'ACCOUNT_OWNER';
  const [vatDialogOpen, setVatDialogOpen] = useState(false);
  const [vatPreview, setVatPreview] = useState(null);
  const [vatLoading, setVatLoading] = useState(false);
  const [vatApplying, setVatApplying] = useState(false);
  const [vatError, setVatError] = useState('');
  const [vatResult, setVatResult] = useState(null);
  const [vatConfirmed, setVatConfirmed] = useState(false);

  const vatStatusLabel = (s) => (s === 'registered' ? 'Registered' : (s === 'not_registered' ? 'Not registered' : 'Unconfirmed'));
  const formatMoney = (value) => `£${Number(value || 0).toFixed(2)}`;
  const formatDate = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

  const openVatReview = async () => {
    setVatDialogOpen(true);
    setVatPreview(null);
    setVatResult(null);
    setVatError('');
    setVatConfirmed(false);
    setVatLoading(true);
    try {
      const response = await api.get(`/suppliers/${formik.values.id}/vat-recalc-preview`);
      setVatPreview(response.data);
    } catch (error) {
      setVatError(error.response?.data?.error || 'Could not load the VAT review. Nothing has been changed.');
    } finally {
      setVatLoading(false);
    }
  };

  const applyVatCorrections = async () => {
    if (!vatPreview || !vatPreview.affected_bills || vatPreview.affected_bills.length === 0) return;
    setVatApplying(true);
    setVatError('');
    try {
      const response = await api.post(`/suppliers/${formik.values.id}/vat-recalc-apply`, {
        bill_ids: vatPreview.affected_bills.map((b) => b.bill_id)
      });
      setVatResult(response.data);
    } catch (error) {
      setVatError(error.response?.data?.error || 'The change could not be completed. Close this window and open the review again to see the current position.');
    } finally {
      setVatApplying(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      id: null,
      type: '',
      supplier_name: '',
      description: '',
      category: '',
      vat_applicable: false,
      vat_status: 'unconfirmed',
      address: '',
      email: '',
      contact_number: '',
      vat_number: '',
      account_number: ''
    },
    validationSchema,
    onSubmit: handleSubmit
  });

  async function handleSubmit(values, { setSubmitting }) {
    try {
      errorHandler.logApiCall('Submitting supplier data:', values);
      let response;
      if (isNewSupplier) {
        response = await api.post('/suppliers', values);
        setSnackbar({ open: true, message: 'New supplier added successfully', severity: 'success' });
      } else {
        response = await api.put(`/suppliers/${values.id}`, values);
        setSnackbar({ open: true, message: 'Supplier updated successfully', severity: 'success' });
      }
      setIsNewSupplier(false);
      formik.setValues(response.data);
      
      // Fetch unpaid bills count after saving
      if (response.data.id) {
        fetchUnpaidBillsCount(response.data.id);
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Error saving supplier', 
        severity: 'error' 
      });
    } finally {
      setSubmitting(false);
    }
  }

  const fetchUnpaidBillsCount = async (supplierId) => {
    try {
      const response = await api.get(`/bills/supplier/${supplierId}`);
      const unpaidCount = response.data.bills?.filter(
        bill => bill.status === 'unpaid' || bill.status === 'partial'
      ).length || 0;
      setUnpaidBillsCount(unpaidCount);
    } catch (error) {
      console.error('Error fetching bills count:', error);
    }
  };

  const handleSearch = async () => {
    try {
      const response = await api.get(`/suppliers/search?name=${searchTerm}&_t=${Date.now()}`);
      console.log('Supplier search response:', response.data);
      
      // Check for the new response format with data array inside a data property
      if (response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
        const supplierData = response.data.data[0];
        formik.setValues({
          id: supplierData.id,
          type: supplierData.type || '',
          supplier_name: supplierData.supplier_name || '',
          description: supplierData.description || '',
          category: supplierData.category || '',
          vat_applicable: supplierData.vat_applicable || false, vat_status: supplierData.vat_status || 'unconfirmed',
          address: supplierData.address || '',
          email: supplierData.email || '',
          contact_number: supplierData.contact_number || '',
          vat_number: supplierData.vat_number || '',
          account_number: supplierData.account_number || ''
        });
        setIsNewSupplier(false);
        setSnackbar({ open: true, message: 'Supplier found', severity: 'success' });
        
        // Fetch unpaid bills count
        if (supplierData.id) {
          fetchUnpaidBillsCount(supplierData.id);
        }
      } else {
        setSnackbar({ open: true, message: 'No supplier found', severity: 'info' });
      }
    } catch (error) {
      console.error('Error searching supplier:', error);
      console.error('Error details:', error.response?.data);
      setSnackbar({ open: true, message: 'Error searching supplier', severity: 'error' });
    }
  };

  const resetForm = () => {
    formik.resetForm();
    setIsNewSupplier(true);
    setUnpaidBillsCount(0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

useEffect(() => {
  const deepLinkSearch = searchParams.get('search');
  const mode = searchParams.get('mode');

  if (mode === 'new') {
    resetForm();
  } else if (deepLinkSearch) {
    setSearchTerm(deepLinkSearch);
    // handleSearch reads searchTerm via closure, so search directly
    // rather than relying on the state update above having landed yet
    (async () => {
      try {
        const response = await api.get(`/suppliers/search?name=${encodeURIComponent(deepLinkSearch)}&_t=${Date.now()}`);
        if (response.data.success && Array.isArray(response.data.data) && response.data.data.length > 0) {
          const supplierData = response.data.data[0];
          formik.setValues({
            id: supplierData.id,
            type: supplierData.type || '',
            supplier_name: supplierData.supplier_name || '',
            description: supplierData.description || '',
            category: supplierData.category || '',
            vat_applicable: supplierData.vat_applicable || false, vat_status: supplierData.vat_status || 'unconfirmed',
            address: supplierData.address || '',
            email: supplierData.email || '',
            contact_number: supplierData.contact_number || '',
            vat_number: supplierData.vat_number || '',
            account_number: supplierData.account_number || ''
          });
          setIsNewSupplier(false);
          if (supplierData.id) {
            fetchUnpaidBillsCount(supplierData.id);
          }
        } else {
          setSnackbar({ open: true, message: `No supplier found matching "${deepLinkSearch}"`, severity: 'info' });
        }
      } catch (error) {
        console.error('Error loading supplier from directory link:', error);
        setSnackbar({ open: true, message: 'Error loading supplier', severity: 'error' });
      }
    })();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []); // run once on mount only
  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>Supplier Card</Typography>
      
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Search supplier by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button fullWidth variant="contained" onClick={handleSearch}>
            Search
          </Button>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button fullWidth variant="outlined" onClick={resetForm}>
            New Supplier
          </Button>
        </Grid>
      </Grid>

      <Tabs 
        value={activeTab} 
        onChange={handleTabChange} 
        sx={{ mb: 3 }}
        variant="fullWidth"
      >
        <Tab label="Supplier Info" />
        <Tab 
          label={
            unpaidBillsCount > 0 ? (
              <Badge badgeContent={unpaidBillsCount} color="error">
                Bills
              </Badge>
            ) : (
              "Bills"
            )
          }
          disabled={!formik.values.id}
        />
      </Tabs>

      {activeTab === 0 && (
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                name="type"
                label="Type *"
                value={formik.values.type}
                onChange={formik.handleChange}
                error={formik.touched.type && Boolean(formik.errors.type)}
                helperText={formik.touched.type && formik.errors.type}
                required
              >
                {SUPPLIER_TYPES.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="supplier_name"
                label="Supplier Name *"
                value={formik.values.supplier_name}
                onChange={formik.handleChange}
                error={formik.touched.supplier_name && Boolean(formik.errors.supplier_name)}
                helperText={formik.touched.supplier_name && formik.errors.supplier_name}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                multiline
                rows={2}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
               <TextField
                 select
                 fullWidth
                 name="category"
                 label="Category"
                 value={formik.values.category}
                 onChange={formik.handleChange}
               >
                <MenuItem value="">— None —</MenuItem>
                 {SUPPLIER_CATEGORIES.map(cat => (
                   <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                 ))}
                </TextField>
           </Grid>
           
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="address"
                label="Address *"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="email"
                label="Email *"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="contact_number"
                label="Contact Number *"
                value={formik.values.contact_number}
                onChange={formik.handleChange}
                error={formik.touched.contact_number && Boolean(formik.errors.contact_number)}
                helperText={formik.touched.contact_number && formik.errors.contact_number}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="vat_number"
                label="VAT Number"
                value={formik.values.vat_number}
                onChange={formik.handleChange}
                helperText="Must be a valid UK VAT number (9 digits, optionally starting GB). Saving with a valid number marks this supplier Registered; saving without one marks it Not Registered, which clears an Unconfirmed status. Leave the VAT number as it is unless you know this supplier is not VAT registered."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="account_number"
                label="Account Number"
                value={formik.values.account_number}
                onChange={formik.handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">VAT Status:</Typography>
                <Chip
                  label={vatStatusLabel(formik.values.vat_status)}
                  color={formik.values.vat_status === 'registered' ? 'success' : (formik.values.vat_status === 'not_registered' ? 'default' : 'warning')}
                  size="small"
              />
            </Box>
          </Grid>

            <Grid item xs={12}>
              <Box sx={{ mt: 3 }}>
                {formik.values.id && !isNewSupplier && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Check the VAT recorded on bills for this supplier that were created from your bank import. You will see exactly what would change before anything is changed.
                    </Typography>
                    <Button variant="outlined" onClick={openVatReview}>
                      Review VAT on this supplier
                    </Button>
                  </Box>
                )}
                <Button type="submit" variant="contained" color="primary" disabled={formik.isSubmitting}>
                  {isNewSupplier ? 'Add Supplier' : 'Update Supplier'}
                </Button>
              </Box>
            </Grid>
          </Grid> 
           </form>
      )}

      {activeTab === 1 && formik.values.id && (
        <SupplierBillsTab 
          supplierId={formik.values.id}
          supplierName={formik.values.supplier_name}
          onBillsChange={(count) => setUnpaidBillsCount(count)}
        />
      )}

      <Dialog open={vatDialogOpen} onClose={() => { if (!vatApplying) setVatDialogOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>Review VAT on this supplier</DialogTitle>
        <DialogContent dividers>
          {vatLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {vatError && <Alert severity="error" sx={{ mb: 2 }}>{vatError}</Alert>}

          {vatResult && (
            <Alert severity="success">
              Done. VAT was updated on {vatResult.applied_count} bill(s), a total change of {formatMoney(vatResult.total_delta)}. Close this window to carry on.
            </Alert>
          )}

          {!vatResult && vatPreview && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {vatPreview.supplier_name}: saved VAT status is{' '}
                <strong>{vatStatusLabel(vatPreview.vat_status)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This only looks at bills created automatically from your bank import. Bills you entered yourself are never changed. Nothing changes until you press the Apply button.
              </Typography>

              {vatPreview.vat_status !== 'registered' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This supplier is not marked as VAT registered, so VAT is not added to its bills and Apply is not offered here. If it does have a VAT number, enter it on the Supplier Info tab, press Update Supplier, then open this review again.
                </Alert>
              )}

              {vatPreview.affected_bills.length === 0 ? (
                <Alert severity="success">No changes are offered for this supplier.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">VAT now</TableCell>
                      <TableCell align="right">VAT after</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vatPreview.affected_bills.map((b) => (
                      <TableRow key={b.bill_id}>
                        <TableCell>{formatDate(b.invoice_date)}</TableCell>
                        <TableCell>{b.invoice_number}</TableCell>
                        <TableCell align="right">{formatMoney(b.total_amount)}</TableCell>
                        <TableCell align="right">{formatMoney(b.old_vat_amount)}</TableCell>
                        <TableCell align="right">{formatMoney(b.new_vat_amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4}><strong>Total change in VAT</strong></TableCell>
                      <TableCell align="right"><strong>{formatMoney(vatPreview.total_delta)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}

              {vatPreview.vat_status === 'registered' && vatPreview.affected_bills.length > 0 && isOwner && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning">
                    VAT added here is included when your VAT return is worked out. Only continue if you hold a valid VAT invoice for each of these payments.
                  </Alert>
                  <FormControlLabel
                    control={<Checkbox checked={vatConfirmed} onChange={(e) => setVatConfirmed(e.target.checked)} />}
                    label="I hold a valid VAT invoice for each of these payments"
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVatDialogOpen(false)} disabled={vatApplying}>
            {vatResult ? 'Close' : 'Cancel'}
          </Button>
          {!vatResult && vatPreview && vatPreview.vat_status === 'registered' && vatPreview.affected_bills.length > 0 && (
            isOwner ? (
              <Button variant="contained" onClick={applyVatCorrections} disabled={!vatConfirmed || vatApplying}>
                {vatApplying ? 'Applying...' : `Apply: change VAT on ${vatPreview.affected_bills.length} bill(s) by ${formatMoney(vatPreview.total_delta)}`}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                Only the account owner can apply this change.
              </Typography>
            )
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SupplierCard;